/**
 * Canvas preview view-state shared between the builder bar and the canvas
 * stage: preview URL (cache-busted), load state, blocked-navigation notice
 * and the theme breakpoint widths. Created once per shell (Builder.vue /
 * BuilderLite.vue) so the bar survives surface switches while the iframe
 * mounts and unmounts with the canvas surface.
 */

import { computed, inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { BuilderBreakpoint } from './types';
import type { BuilderHost, BuilderSurface, BuilderTranslator } from './host';
import type { BuilderStore } from './useBuilderStore';

export interface CanvasChrome {
    /** Only reload() changes this — the iframe src must never churn. */
    previewUrl: ComputedRef<string>;
    state: Ref<'loading' | 'ready' | 'error'>;
    blockedNotice: Ref<boolean>;
    /** Theme breakpoint widths resolved from the host's PreviewSource. */
    breakpoints: Ref<{ tablet: number; mobile: number }>;
    reload(): void;
}

export interface CanvasChromeDependencies {
    host: BuilderHost;
    store: BuilderStore;
    /** Bumped by the bridge on blocked in-iframe navigation. */
    lastBlockedUrl: Ref<string | null>;
    /** Active surface; absent (lite) = always canvas. */
    surface?: Ref<BuilderSurface>;
}

const CANVAS_CHROME_KEY: InjectionKey<CanvasChrome> = Symbol('builder-v2-canvas-chrome');

const READY_TIMEOUT_MS = 10_000;
const BLOCKED_NOTICE_MS = 3_000;

export function createCanvasChrome(deps: CanvasChromeDependencies): CanvasChrome {
    const { host, store } = deps;
    const state = ref<CanvasChrome['state']['value']>('loading');
    const cacheBust = ref(0);
    const blockedNotice = ref(false);
    const breakpoints = ref<{ tablet: number; mobile: number }>({ tablet: 768, mobile: 375 });
    let readyTimer: ReturnType<typeof setTimeout> | null = null;
    let blockedTimer: ReturnType<typeof setTimeout> | null = null;

    const previewUrl = computed(() => host.preview.previewUrl(cacheBust.value));

    const clearReadyTimer = (): void => {
        if (readyTimer) {
            clearTimeout(readyTimer);
            readyTimer = null;
        }
    };

    const armReadyTimeout = (): void => {
        clearReadyTimer();

        readyTimer = setTimeout(() => {
            if (state.value === 'loading') {
                state.value = 'error';
            }
        }, READY_TIMEOUT_MS);
    };

    const reload = (): void => {
        store.bridgeReady.value = false;
        state.value = 'loading';
        cacheBust.value += 1;
        armReadyTimeout();
    };

    watch(store.bridgeReady, (ready) => {
        if (ready) {
            state.value = 'ready';
            clearReadyTimer();
        }
    });

    watch(deps.lastBlockedUrl, () => {
        blockedNotice.value = true;

        if (blockedTimer) {
            clearTimeout(blockedTimer);
        }

        blockedTimer = setTimeout(() => (blockedNotice.value = false), BLOCKED_NOTICE_MS);
    });

    // Document surface has no iframe — the ready timeout must not tick into
    // an error there. Re-entering canvas reloads: the patch loop was silent
    // while the document surface edited, the iframe content is stale.
    if (deps.surface) {
        watch(deps.surface, (next, previous) => {
            if (next === 'canvas' && previous === 'document') {
                reload();
            } else if (next === 'document') {
                clearReadyTimer();
            }
        });
    }

    onMounted(async () => {
        if ((deps.surface?.value ?? 'canvas') === 'canvas') {
            armReadyTimeout();
        }

        const resolved = await host.preview.breakpoints().catch(() => null);

        if (resolved) {
            breakpoints.value = resolved;
        }
    });

    onBeforeUnmount(() => {
        clearReadyTimer();

        if (blockedTimer) {
            clearTimeout(blockedTimer);
        }
    });

    const chrome: CanvasChrome = { previewUrl, state, blockedNotice, breakpoints, reload };

    provide(CANVAS_CHROME_KEY, chrome);

    return chrome;
}

export function useCanvasChromeOptional(): CanvasChrome | null {
    return inject(CANVAS_CHROME_KEY, null);
}

/** Breakpoint switcher options — one list for the bar and the stage math. */
export function breakpointOptions(
    t: BuilderTranslator,
    widths: { tablet: number; mobile: number },
): { key: BuilderBreakpoint; label: string; width: number | null; icon: string }[] {
    return [
        { key: 'desktop', label: t('breakpoint_desktop'), width: null, icon: 'phosphor/regular/desktop' },
        { key: 'tablet', label: t('breakpoint_tablet'), width: widths.tablet, icon: 'phosphor/regular/device-tablet' },
        { key: 'mobile', label: t('breakpoint_mobile'), width: widths.mobile, icon: 'phosphor/regular/device-mobile' },
    ];
}
